import styled from '@emotion/styled/macro'
import colors from '@sportninja/common/constants/appColors'
import { Tooltip } from 'antd'
import React from 'react'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import { weightMedium, weightRegular } from 'src/components/css'
import HockeyCanadaLogo from './HockeyCanadaLogo'
import HockeyCanadaWarningModal from './HockeyCanadaWarningModal'

interface HockeyCanadaIntegrationCardProps {
  /** Whether the user has an Ultimate account */
  isUltimateAccount: boolean
  /** Whether the Hockey Canada integration is connected */
  isHockeyCanadaConnected: boolean
  /** Whether the Hockey Canada integration is enabled */
  isHockeyCanadaEnabled: boolean
  /** Whether the cost-confirmation modal is visible */
  showHockeyCanadaWarning: boolean
  /** Whether an enable/disable request is in flight */
  isUpdatingHockeyCanada: boolean
  /** Toggles the integration on or off */
  handleHockeyCanadaToggle: (enabled: boolean) => void
  /** Confirms enabling after the cost warning */
  confirmHockeyCanadaEnable: () => void
  /** Dismisses the cost warning without enabling */
  cancelHockeyCanadaEnable: () => void
  /** Navigates to the plan-upgrade page */
  setCurrentPage: (page: number) => void
  /** Display name of the current subscription tier */
  accountTypeName: string
}

/**
 * Account-level Hockey Canada integration card.
 *
 * Renders the integration tile in General Settings → Integrations and the
 * cost-confirmation modal shown before enabling. Presentation only — all state
 * and API calls live in `useHockeyCanadaIntegration`.
 */
const HockeyCanadaIntegrationCard: React.FC<
  HockeyCanadaIntegrationCardProps
> = ({
  isUltimateAccount,
  isHockeyCanadaConnected,
  isHockeyCanadaEnabled,
  showHockeyCanadaWarning,
  isUpdatingHockeyCanada,
  handleHockeyCanadaToggle,
  confirmHockeyCanadaEnable,
  cancelHockeyCanadaEnable,
  setCurrentPage,
  accountTypeName,
}) => {
  const upgradeTooltip = (
    <TooltipBody>
      You are currently using a <TierName>{accountTypeName}</TierName> account.
      <br />
      Upgrade your account to get all the features on the SportNinja platform.{' '}
      <UpgradeLink
        onClick={(e) => {
          e.stopPropagation()
          setCurrentPage(1)
        }}
      >
        Click here to upgrade
      </UpgradeLink>
    </TooltipBody>
  )

  return (
    <>
      <Tooltip title={!isUltimateAccount ? upgradeTooltip : ''} placement='top'>
        <Card
          $isConnected={isHockeyCanadaConnected}
          $isLocked={!isUltimateAccount}
          onClick={() => {
            if (!isUltimateAccount) {
              setCurrentPage(1)
            }
          }}
        >
          <CardHeader>
            {/* Sized by height so this near-square mark matches the visual
                weight of the wide USA Hockey wordmark on the sibling card. */}
            <HockeyCanadaLogo height={60} />
            <PlanBadge>Included in Ultimate</PlanBadge>
          </CardHeader>

          <Description>
            Validate Hockey Canada member numbers for players during
            registration and ensure compliance with Hockey Canada requirements.
          </Description>

          <SwitchRow>
            <SwitchInput
              title={capitalizeWords('Enable Integration')}
              subtitle='Allow Hockey Canada member validation'
              value={isHockeyCanadaEnabled}
              onChange={handleHockeyCanadaToggle}
              disabled={!isUltimateAccount || isUpdatingHockeyCanada}
            />
          </SwitchRow>
        </Card>
      </Tooltip>

      <HockeyCanadaWarningModal
        open={showHockeyCanadaWarning}
        isUpdating={isUpdatingHockeyCanada}
        onConfirm={confirmHockeyCanadaEnable}
        onCancel={cancelHockeyCanadaEnable}
      />
    </>
  )
}

export default HockeyCanadaIntegrationCard

// ---- styled ----

const TooltipBody = styled.span`
  font-size: 12px;
`

const TierName = styled.b`
  color: ${colors.PRIMARY};
`

const UpgradeLink = styled.a`
  color: ${colors.PRIMARY};
  text-decoration: underline;
  cursor: pointer;
`

const Card = styled.div<{ $isConnected: boolean; $isLocked: boolean }>`
  display: flex;
  padding: 16px;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  min-height: 180px;
  border-radius: 4px;
  border: ${({ $isConnected }) =>
    $isConnected
      ? `1px dashed ${colors.SOFT_STEEL}`
      : `1px solid ${colors.SOFT_STEEL}`};
  background-color: transparent;
  transition: background-color 0.2s ease;

  ${({ $isLocked }) =>
    $isLocked &&
    `
      opacity: 0.6;
      cursor: pointer;
      &:hover {
        background-color: rgba(255, 255, 255, 0.05);
      }
    `}
`

const CardHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const PlanBadge = styled.span`
  color: ${colors.PRIMARY_200};
  text-align: right;
  font-size: 12px;
  line-height: 100%;
  ${weightRegular}
`

const Description = styled.span`
  align-self: stretch;
  color: ${colors.WHITE};
  font-size: 12px;
  line-height: 120%;
  width: 100%;
  margin-top: 16px;
  ${weightMedium}
`

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 16px;
  border-top: 1px solid ${colors.SOFT_STEEL};
  margin-top: 16px;
`
